import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    ScheduledWord,
    WordFlowBatch,
    getWordFlow,
    hasReachedMessageLimit,
    isWarningVisible,
    wordFlowBatchReceived,
    wordUpvoted,
    getTimeAndProgressLeft,
    stopCalculatingProgress,
} from 'redux/slices/wordFlow'
import {
    closeWarningMsg,
    submitWord,
    resume as resumeWordFlow,
    startCalculatingProgress,
} from 'redux/slices/wordFlow/thunks'
import { useDispatch, useSelector } from 'react-redux'

import Container from 'react-bootstrap/Container'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import { WEBSOCKET_WORD_FLOW_BATCH } from 'constants.js'
import clsx from 'clsx'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { openModal, ModalContent } from 'redux/slices/globalModal'
import { rand } from 'utils'
import { useStyles } from './styles'
import useWebSocketEvent from 'hooks/useWebSocketEvent'
import warningIcon from 'assets/notice-icon.svg'
import CountdownBar from 'components/common/CountdownBar/CountdownBar'
import { useAuthenticatedState } from 'libs/web-client/utils/auth0/src'

interface WordFlowItem {
    word: ScheduledWord
    travelDistance: number
    adminView: boolean
}

const WordFlowItem: React.FC<WordFlowItem> = React.memo(
    ({ word, travelDistance, adminView }) => {
        if (word.endsAt < Date.now()) return null

        const { isAuthenticated, user } = useAuthenticatedState()

        const classes = useStyles()
        const [offsetX] = useState(`${rand(0, 100)}%`)
        const isHighlighted = word.user === user?._id || word.upvotes.includes(user?._id)

        const dispatch = useDispatch()

        const upVote = useCallback(() => {
            if (isAuthenticated && !adminView) {
                dispatch(wordUpvoted({ word, user }))
            }
        }, [dispatch, isAuthenticated, user, word, adminView])

        const zIndex = Math.round(word.weight * 1000)
        const opacity = Math.max(word.weight, 0.2)

        return (
            <li
                className={clsx('wordflow-item', classes.wordItem)}
                style={
                    {
                        zIndex,
                        width: offsetX,
                        animationDuration: `${word.duration}ms`,
                        animationDelay: `${word.delay}ms`,
                        '--opacity': opacity,
                        '--travelDistance': `-${travelDistance || 600}px`,
                    } as React.CSSProperties
                }
            >
                <button className={clsx(classes.word, isHighlighted && classes.wordHighlighted)} onClick={upVote}>
                    {word.message}
                </button>
            </li>
        )
    },
    (prevProps, nextProps) => prevProps.word === nextProps.word,
)

interface WordFlowProps {
    adminView?: boolean
    durationModifier?: number
    minSpeed?: number
    onClose?: () => void
}

const WordFlow: React.FC<WordFlowProps> = ({
    adminView = false,
    durationModifier = 0,
    minSpeed = 0.75,
    onClose = () => {},
}) => {
    const classes = useStyles()

    const [newWord, setNewWord] = useState('')
    const [travelDistance, setTravelDistance] = useState(0)

    const words = useSelector(getWordFlow)
    const { isAuthenticated, user } = useAuthenticatedState()

    const dispatch = useDispatch()

    const contentRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLFormElement | null>(null)

    const warningVisible = useSelector(isWarningVisible)
    const reachedMessageLimit = useSelector(hasReachedMessageLimit)
    const [showMessageLimitReached, setMessageLimitReached] = useState(false)

    const { timeLeft, currentProgress } = useSelector(getTimeAndProgressLeft)

    const dismissWarning = useCallback(() => dispatch(closeWarningMsg()), [dispatch])

    useEffect(() => setTravelDistance(contentRef.current?.offsetHeight || 0), [])

    useEffect(() => {
        dispatch(resumeWordFlow())
        dispatch(startCalculatingProgress())
        return () => {
            dispatch(stopCalculatingProgress())
        }
    }, [dispatch])

    useWebSocketEvent(WEBSOCKET_WORD_FLOW_BATCH, (batch: WordFlowBatch) => {
        dispatch(
            wordFlowBatchReceived({ batch, durationModifier, minSpeed, userId: isAuthenticated ? user._id : null }),
        )
    })

    return (
        <div className={classes.wordFlow}>
            <CountdownBar currentProgress={currentProgress} timeLeft={timeLeft} />
            {!adminView && (
                <header className={classes.header}>
                    <button id="wordflow-back-button" className={classes.closeBtn} onClick={onClose}>
                        <FontAwesomeIcon className={classes.backBtnIcon} icon={faChevronLeft} />
                        <span className={classes.backBtnText}>Back</span>
                    </button>
                </header>
            )}
            {!adminView && warningVisible && (
                <p className={classes.warningBanner}>
                    <img className={classes.warningIcon} src={warningIcon} alt="" />
                    <span>Submit a response! Be thoughtful, you only get 1 submission for this round of responses</span>
                    <button className={classes.dismissButton} onClick={dismissWarning}>
                        &times;
                    </button>
                </p>
            )}
            <div className={classes.content} ref={contentRef}>
                <ul id="wordflow-list" className={classes.wordList}>
                    {words.map(word => (
                        <WordFlowItem
                            word={word}
                            key={word._id}
                            travelDistance={travelDistance}
                            adminView={adminView}
                        />
                    ))}
                </ul>
            </div>
            {!adminView && (
                <footer className={classes.footer} ref={footerRef}>
                    <form
                        id="wordflow-form"
                        className={classes.form}
                        onSubmit={e => {
                            e.preventDefault()

                            if (!isAuthenticated) {
                                dispatch(openModal({ content: ModalContent.SIGNUP }))
                                return
                            }

                            if (!reachedMessageLimit && newWord) {
                                dispatch(submitWord(newWord, user._id))
                                setNewWord('')
                            } else {
                                setMessageLimitReached(true)
                            }
                        }}
                    >
                        <input
                            className={classes.input}
                            placeholder="Respond"
                            onChange={e => setNewWord(e.target.value)}
                            value={newWord}
                            maxLength={30}
                        />
                        <button className={classes.submitBtn} type="submit" id="wordflow-submit">
                            <FontAwesomeIcon icon={faPaperPlane} color="#D3D5DB" />
                        </button>
                    </form>
                </footer>
            )}
            {!adminView && footerRef.current && (
                <Overlay
                    target={footerRef.current}
                    show={showMessageLimitReached}
                    placement="top"
                    rootClose
                    onHide={() => setMessageLimitReached(false)}
                >
                    <Popover id="max-submit-popover">
                        <Container>
                            <div className="body-2">
                                You are only allowed to submit 1 message for this round of responses
                            </div>
                        </Container>
                    </Popover>
                </Overlay>
            )}
        </div>
    )
}

export default React.memo(WordFlow)
